import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Snackbar } from '../../../../shared/components/Snackbar';
import { useAuthViewport } from './AuthTemplate/useAuthViewport';
import { ForgotPassword } from '../lazyAuthRoutes';
import { preloadAfterLogin } from '@/app/routes/lazyPages';
import AuthTemplate from './AuthTemplate/index';
import LoginForm from './loginForm';
import OtpForm from './otpForm';

type AuthView = 'login' | 'otp';

const LoginPage = () => {
  const { isTablet } = useAuthViewport();
  const [currentView, setCurrentView] = useState<AuthView>('login');
  const [userEmail, setUserEmail] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Warm the forgot-password route once the login page is idle. Its Suspense
  // fallback is null, so without this the first click on "Zapomniałeś hasła?"
  // shows a blank white page while the route loads.
  useEffect(() => {
    const scheduleLoad =
      window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 500));

    scheduleLoad(() => {
      // A failed preload is harmless: the route loads again on click
      ForgotPassword.preload().catch(() => {});
    });
  }, []);

  // Detect logout status from URL
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'logged-out') {
      setSnackbarMessage('Zostałeś(-aś) wylogowany(-a).');
      setShowSuccessSnackbar(true);

      // Clear the parameter from the URL without reloading
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLoginSuccess = useCallback((email: string) => {
    // Credentials accepted: fetch the landing page while the user types the
    // OTP code, so the first screen after login has no loader
    preloadAfterLogin();
    setUserEmail(email);
    setSnackbarMessage(
      'Jednorazowy kod został wysłany na Twój adres e-mail. Sprawdź skrzynkę odbiorczą.'
    );
    setShowSuccessSnackbar(true);
    setCurrentView('otp');
  }, []);

  const handleBackToLogin = useCallback(() => {
    setCurrentView('login');
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setShowSuccessSnackbar(false);
  }, []);

  // Memoize FormComponent to prevent unnecessary re-creation
  const FormComponent = useMemo(() => {
    return currentView === 'login'
      ? () => (
          <LoginForm onLoginSuccess={handleLoginSuccess} isTablet={isTablet} />
        )
      : () => (
          <OtpForm
            email={userEmail}
            onBack={handleBackToLogin}
            isTablet={isTablet}
          />
        );
  }, [currentView, userEmail, handleLoginSuccess, handleBackToLogin, isTablet]);

  return (
    <>
      <AuthTemplate FormComponent={FormComponent} />

      <Snackbar
        open={showSuccessSnackbar}
        message={snackbarMessage}
        variant="success"
        onClose={handleSnackbarClose}
        autoHideDuration={15000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};

export default LoginPage;
